/**
 * The cross-section's SERIES model (section-overlays spec §3).
 *
 * Everything the section draws is a series: one plane, one role, one row per
 * station line. The run's own file gives `asbuilt` and `design` series
 * (roles from the plane-name prefixes, exactly as `sectionModel.ts`
 * classifies them); an overlays file gives `reference` series. Bands, hatches
 * and tolerance runs are computed from `asbuilt`/`design` ONLY — a reference
 * takes part in the span, the gaps, the legend, the readout and the exports,
 * never in a claim.
 *
 * `rowAt(k)` answers on the run's own offset grid whichever file the series
 * came from; `k` is a station-line index for true sections and a BIN index
 * for the bin-mean fallback — the caller picks, as the chart always has.
 */
import { asBuiltPlaneIndices, designPlaneIndices, levelName, planeRowAt } from './sectionModel';
import type { SectionOverlaySetting } from './sectionOverlays';
import type { SectionOverlays, SeriesRole, StationOffsets, StationSections } from './types';

export type { SeriesRole } from './types';
export type SeriesFile = 'sections' | 'offsets' | 'overlays';

export interface SectionSeries {
  /** The plane name in its file — `asbuilt:<level>`, `zTop`, or an overlay id. */
  id: string;
  label: string;
  role: SeriesRole;
  file: SeriesFile;
  /**
   * The ink a REFERENCE is drawn in. Absent on the run's own series, which
   * are drawn in the neutral surface ink: colour on this chart means "this
   * line came from somewhere else", and giving the as-built a hue would claim
   * the survey is a category.
   */
  color?: string;
  /** One station line's elevations. NaN is no data. Clamps `k` like `sectionLineAt`. */
  rowAt(k: number): Float32Array;
}

const clampRow = (plane: Float32Array, k: number, nRows: number, nOffsets: number): Float32Array =>
  planeRowAt(plane, Math.max(0, Math.min(nRows - 1, k)), nOffsets);

/**
 * The run's own series. True sections win when present; otherwise the two
 * bin-mean planes stand in as the as-built pair, exactly as the chart's
 * fallback always did. Neither → `[]`.
 */
export function runSeries(sections: StationSections | null, offsets: StationOffsets | null): SectionSeries[] {
  if (sections && Array.isArray(sections.planeNames) && sections.planeNames.length >= 2) {
    const names = sections.planeNames;
    const mk = (i: number, role: SeriesRole): SectionSeries => ({
      id: names[i],
      label: levelName(names[i]),
      role,
      file: 'sections',
      rowAt: (k) => clampRow(sections.planes[names[i]], k, sections.nStationLines, sections.nOffsets),
    });
    return [
      ...asBuiltPlaneIndices(names).map((i) => mk(i, 'asbuilt')),
      ...designPlaneIndices(names).map((i) => mk(i, 'design')),
    ];
  }
  if (sections?.zTop && sections.zBottom) {
    // A sections object that names no planes still carries the pair: `zTop`
    // and `zBottom` are the decoder's own first two, kept for every caller
    // written before the stack. The chart drew exactly this pair off them
    // before it read series, and it is a TRUE section — the station-line
    // planes of this same file — so it must not fall through to the
    // bin means below, which answer on a different index.
    const mk = (id: 'zTop' | 'zBottom'): SectionSeries => ({
      id,
      label: id,
      role: 'asbuilt',
      file: 'sections',
      rowAt: (k) => clampRow(sections[id], k, sections.nStationLines, sections.nOffsets),
    });
    return [mk('zTop'), mk('zBottom')];
  }
  if (offsets) {
    const mk = (id: 'zTop' | 'zBottom'): SectionSeries => ({
      id,
      label: id,
      role: 'asbuilt',
      file: 'offsets',
      rowAt: (k) => clampRow(offsets[id], k, offsets.nStations, offsets.nOffsets),
    });
    return [mk('zTop'), mk('zBottom')];
  }
  return [];
}

export interface SeriesRows {
  asbuilt: Float32Array[];
  asbuiltIds: string[];
  asbuiltLabels: string[];
  design: Float32Array[];
  reference: { series: SectionSeries; row: Float32Array }[];
}

/** Every series' row at one station line, partitioned by role. */
export function seriesRowsAt(series: readonly SectionSeries[], k: number): SeriesRows {
  const out: SeriesRows = { asbuilt: [], asbuiltIds: [], asbuiltLabels: [], design: [], reference: [] };
  for (const s of series) {
    const row = s.rowAt(k);
    if (s.role === 'asbuilt') {
      out.asbuilt.push(row);
      out.asbuiltIds.push(s.id);
      out.asbuiltLabels.push(s.label);
    } else if (s.role === 'design') out.design.push(row);
    else out.reference.push({ series: s, row });
  }
  return out;
}

/**
 * Which two AS-BUILT rows the dock's chip selected. `[0, 1]` when the pair is
 * absent (every single-pair run) or names a plane the file does not have —
 * the decoder's own first two, never a blank section.
 */
export function activePairIndices(rows: SeriesRows, planePair: readonly [string, string] | null): [number, number] {
  if (!planePair) return [0, 1];
  const a = rows.asbuiltIds.indexOf(planePair[0]);
  const b = rows.asbuiltIds.indexOf(planePair[1]);
  return a >= 0 && b >= 0 ? [a, b] : [0, 1];
}

/**
 * Reference series from the overlays file: one per VISIBLE setting whose
 * plane the file has.
 *
 * The ink index is the setting's place in the FULL list, hidden entries
 * included, so hiding one never recolours the others — a legend whose colours
 * shuffle when you toggle an unrelated row is not a legend.
 *
 * A setting the file has no plane for is simply absent: the CALLER decides
 * what to say about it (`overlayStates`), because "pending" and "failed" are
 * different sentences and neither is a line on the drawing.
 */
export function overlaySeries(
  overlays: SectionOverlays | null,
  settings: readonly SectionOverlaySetting[],
  inks: readonly string[],
): SectionSeries[] {
  if (!overlays) return [];
  const out: SectionSeries[] = [];
  settings.forEach((s, i) => {
    if (s.hidden) return;
    const meta = overlays.meta.planes.find((p) => p.id === s.id);
    const plane = overlays.planes[s.id];
    if (!meta || !plane) return;
    const { nStationLines, nOffsets } = overlays.meta;
    out.push({
      id: s.id,
      label: meta.label,
      role: meta.role,
      file: 'overlays',
      color: inks[i % inks.length],
      rowAt: (k) => clampRow(plane, k, nStationLines, nOffsets),
    });
  });
  return out;
}
